// v2 cockpit — « Ventes vs pub » (owner, 2026-10-22): 14 daily bars of encaissé
// with the ad spend as a line, last 14 Casablanca days. Inline SVG, no chart
// library. A day the ad sync cannot vouch for is a gap in the line, never 0.
import type { SeriesPoint } from '@/lib/dashboardApi';
import { formatNum, salesChartGeometry } from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { HomeCard, HomeCardTitle } from './homeParts';

const W = 280;
const H = 104;
const AD = 'var(--color-status-waiting)';

function shortDay(day: string, lang: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' }).replace('.', '');
}

export function SalesChart({ series }: { series: SeriesPoint[] }) {
  const { t, lang } = useT();
  const g = salesChartGeometry(series, W, H);
  const last = series.length - 1;
  const totalRev = series.reduce((s, p) => s + p.revenue, 0);
  const knownAds = series.filter((p) => p.adSpend != null);
  const totalAds = knownAds.reduce((s, p) => s + (p.adSpend ?? 0), 0);
  const summary = t('14 jours : {r} DH encaissés, {a} DH de pub', { r: formatNum(totalRev), a: knownAds.length ? formatNum(totalAds) : '—' });
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Stretches with the card (the to-do list beside it sets the row height). */}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full flex-1 min-h-[96px]" role="img" aria-label={summary}>
        <title>{summary}</title>
        {g.bars.map((b, i) => (
          <rect key={series[i].day} x={b.x} y={b.y} width={b.width} height={Math.max(0, b.height)} rx={2}
            fill="#C9A24A" fillOpacity={i === last ? 1 : 0.45}>
            <title>{`${shortDay(series[i].day, lang)} · ${formatNum(series[i].revenue)} DH · ${t('pub')} ${series[i].adSpend != null ? `${formatNum(series[i].adSpend!)} DH` : '—'}`}</title>
          </rect>
        ))}
        {g.line.map((pts, i) => (
          <polyline key={i} points={pts} fill="none" style={{ stroke: AD }} strokeWidth={1.8} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        ))}
        <line x1={0} y1={H - 0.5} x2={W} y2={H - 0.5} style={{ stroke: 'var(--color-adm-line)' }} vectorEffect="non-scaling-stroke" />
      </svg>
      {series.length > 0 && (
        <div className="flex justify-between text-[10px] text-adm-faint mt-0.5">
          <span>{shortDay(series[0].day, lang)}</span>
          <span>{shortDay(series[last].day, lang)}</span>
        </div>
      )}
    </div>
  );
}

export function HomeSalesChart({ series, isLoading, className = '' }: { series?: SeriesPoint[]; isLoading: boolean; className?: string }) {
  const { t } = useT();
  if (!isLoading && !series) return null;
  const legend = (
    <div className="flex gap-3 text-[11px] text-adm-muted whitespace-nowrap">
      <span><i aria-hidden className="inline-block w-2 h-2 rounded-[2px] mr-1 align-middle bg-adm-gold" />{t('Encaissé')}</span>
      <span><i aria-hidden className="inline-block w-2 h-2 rounded-[2px] mr-1 align-middle bg-status-waiting" />{t('Pub')}</span>
    </div>
  );
  return (
    <HomeCard className={`flex flex-col ${className}`}>
      <HomeCardTitle label={t('Ventes vs pub')} right={legend} />
      {isLoading || !series ? <div aria-busy className="h-[110px] rounded-lg bg-adm-surface-3 animate-pulse" /> : <SalesChart series={series} />}
    </HomeCard>
  );
}
